import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const ColorMapImportDialog = ({ open, onOpenChange, onImport }) => {
  const [heightFile, setHeightFile] = useState(null);
  const [colorFile, setColorFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const close = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setHeightFile(null);
      setColorFile(null);
    }
  };

  const importPair = async () => {
    if (!heightFile || !colorFile) {
      toast.error("Select both the height map and its matching color map.");
      return;
    }
    setIsImporting(true);
    try {
      const imported = await onImport(heightFile, colorFile);
      if (imported) close(false);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="import-color-map-dialog" className="max-w-xl bg-[#101116] border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle>Import an existing color map</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Select both files together. The height map and color map must have identical dimensions so every pixel stays aligned.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <FileField
            label="Matching height map"
            testId="import-height-map-input"
            file={heightFile}
            onChange={setHeightFile}
          />
          <FileField
            label="Existing color map"
            testId="import-color-map-input"
            file={colorFile}
            onChange={setColorFile}
          />
        </div>

        <Button
          data-testid="confirm-import-color-map"
          onClick={importPair}
          disabled={!heightFile || !colorFile || isImporting}
          className="w-full brand-gradient text-white font-semibold"
        >
          {isImporting ? "Importing…" : "Import matched maps"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const FileField = ({ label, testId, file, onChange }) => (
  <label className="block space-y-2">
    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
    <input
      data-testid={testId}
      type="file"
      accept="image/*"
      onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      className="block w-full rounded-md border border-zinc-800 bg-[#131418] px-3 py-2 text-sm text-zinc-300 file:mr-3 file:rounded file:border-0 file:bg-zinc-800 file:px-3 file:py-1 file:text-xs file:font-medium file:text-zinc-100 hover:file:bg-zinc-700"
    />
    {file && <span className="block truncate text-xs text-zinc-500">{file.name}</span>}
  </label>
);
